/**
 * Verständlichkeits-Welle — zentrales Glossar.
 *
 * Übersetzt Trader-Slang/Anglizismen in Alltagsdeutsch und liefert
 * pro Begriff eine kurze Klartext-Erklärung. Wird von UI-Komponenten
 * via TermLabel/TermTooltip eingebunden — beim ersten Auftreten der
 * volle Begriff in Klammern, sonst nur Kurzform mit Tooltip.
 *
 * Grundsatz: Jeder Begriff muss erklärt werden — kein Slang ohne Kontext.
 */

export interface GlossaryEntry {
  /** Original-/Slang-Begriff (z.B. "52W-Hoch"). */
  short: string;
  /** Klartext-Übersetzung (z.B. "Jahreshoch"). */
  label: string;
  /** Kurze Erklärung in einem Satz (für Tooltips / Klammer-Texte). */
  explanation: string;
}

export const GLOSSARY: Record<string, GlossaryEntry> = {
  // ===== Kursebenen =====
  high_52w: {
    short: '52W-Hoch',
    label: 'Jahreshoch',
    explanation: 'Höchster Kurs der letzten 12 Monate.',
  },
  low_52w: {
    short: '52W-Tief',
    label: 'Jahrestief',
    explanation: 'Niedrigster Kurs der letzten 12 Monate.',
  },

  // ===== Gleitende Durchschnitte =====
  ma50: {
    short: 'MA50',
    label: '50-Tage-Durchschnitt',
    explanation: 'Durchschnittskurs der letzten 50 Handelstage — kurzfristiger Trend-Indikator.',
  },
  ma200: {
    short: 'MA200',
    label: '200-Tage-Durchschnitt',
    explanation: 'Langfristiger Trend-Indikator über die letzten 200 Handelstage.',
  },
  ema50: {
    short: 'EMA50',
    label: 'Gleitender 50-Tage-Durchschnitt',
    explanation: 'Wie MA50, aber neuere Tage werden stärker gewichtet.',
  },
  ma40_weekly: {
    short: 'MA40W',
    label: '40-Wochen-Durchschnitt',
    explanation: 'Wöchentlicher Langfrist-Trend (≈ 200-Tage-Schnitt) — Stage-2-Indikator.',
  },

  // ===== Volatilität =====
  atr: {
    short: 'ATR',
    label: 'Durchschnittliche Tagesschwankung',
    explanation: 'Wie stark bewegt sich die Aktie pro Tag im Schnitt? Hohe ATR = volatil.',
  },

  // ===== Backtest-Kennzahlen =====
  avg_r: {
    short: 'Avg-R',
    label: 'Durchschnittlicher Gewinn pro Trade',
    explanation: 'In Vielfachen des Risikos. +1.0 R bedeutet: pro 1 € Risiko gab es 1 € Gewinn.',
  },
  profit_factor: {
    short: 'Profit-Factor',
    label: 'Gewinn-Verlust-Verhältnis',
    explanation: 'Summe der Gewinne geteilt durch Summe der Verluste. > 1 = profitabel.',
  },
  win_rate: {
    short: 'Win-Rate',
    label: 'Trefferquote',
    explanation: 'Anteil der Trades die im Plus geendet sind.',
  },
  drawdown: {
    short: 'Drawdown',
    label: 'Maximaler Rückgang vom Höchststand',
    explanation: 'Der größte Wertverlust seit dem letzten Konto-Hoch — wie tief war der schlimmste Einbruch?',
  },
  is_oos: {
    short: 'IS / OOS',
    label: 'Trainingsphase / Testphase',
    explanation: 'IS = Zeitraum zum Entwickeln/Optimieren, OOS = unabhängiger Test danach.',
  },

  // ===== Score / Datenqualität =====
  score: {
    short: 'Score',
    label: 'Gesamt-Bewertung',
    explanation: 'Skala 0–100. Höher = mehr Faktoren sprechen für einen Einstieg.',
  },
  data_trust: {
    short: 'Datenvertrauen',
    label: 'Datenvollständigkeit',
    explanation: 'Wie viele der nötigen Kennzahlen wir aktuell für dieses Symbol berechnen können.',
  },
  rr: {
    short: 'R:R',
    label: 'Chance-Risiko-Verhältnis',
    explanation: '1:1.5 bedeutet: für 1 € Risiko erwartest du 1.50 € Gewinn — je höher, desto besser.',
  },
  rs_rating: {
    short: 'RS',
    label: 'Relative Stärke',
    explanation: 'Wie stark läuft diese Aktie im Vergleich zum Gesamtmarkt? Skala 0–100, 80+ = Top-Performer.',
  },

  // ===== Marktphasen =====
  market_phase: {
    short: 'Marktphase',
    label: 'Marktphase',
    explanation:
      'Aufwärtstrend, Korrektur, Abwärtstrend, Bodenbildung oder unklar — bestimmt welche Strategien sinnvoll sind.',
  },
  market_regime: {
    short: 'Marktregime',
    label: 'Marktregime',
    explanation:
      'Risk-On (Anleger gehen Risiko) vs. Risk-Off (Flucht in sichere Häfen) je Asset-Klasse.',
  },

  // ===== W36 — Begriffe aus Welle 32/33 + M12-M16 (User-Feedback 2026-05-17) =====
  conviction: {
    short: 'Conviction',
    label: 'Überzeugung',
    explanation:
      '0–100 %: wie sicher bist Du dass die These trägt? Sinkt automatisch wenn die KI-Wochenprüfung neue Risikofakten findet.',
  },
  pillars: {
    short: 'Pillars',
    label: 'Stützen der These',
    explanation:
      'Was muss eintreten damit die Position aufgeht? 3–5 konkrete, messbare Erwartungen — z.B. „Cloud-Wachstum >25% YoY".',
  },
  risks: {
    short: 'Risks',
    label: 'Widerlegungs-Risiken',
    explanation:
      'Was würde die These klar widerlegen? Pro Risk eine Schwelle — z.B. „Op-Margin <20%" oder „Wochenschluss <MA50". Hilft FOMO zu vermeiden.',
  },
  falsifiability: {
    short: 'Falsifizierbarkeit',
    label: 'Widerlegbarkeit',
    explanation:
      'Eine These ist nur dann tragfähig wenn klar ist welche Beobachtung sie KIPPEN würde. Ohne klares Stop-Kriterium läuft man in jeden Hype.',
  },
  drift: {
    short: 'Drift',
    label: 'Drift-Risiko',
    explanation:
      'Hat sich die fundamentale Lage seit Position-Eröffnung verschoben? Wird wöchentlich durch KI-Check geprüft (M26).',
  },
  adherence: {
    short: 'Adherence',
    label: 'Plan-Treue',
    explanation:
      'Wie konsequent hast Du den Plan eingehalten? Vergleich Entry/Stop/TP zwischen geplant und tatsächlich — pro Komponente 0-100.',
  },
  phantom_plan: {
    short: 'Phantom-Plan',
    label: 'Simulierter Trade',
    explanation:
      'Wäre ein Trade-Plan beim aktuellen Score automatisch entstanden? Wir tracken Outcome ohne echtes Geld — Strategie-Validierung.',
  },
  trailing_stop: {
    short: 'Trailing-Stop',
    label: 'Mitziehender Stop',
    explanation:
      'Nach Erreichen von TP1 wandert der Stop auf Entry (Breakeven), nach TP2 auf TP1. Verluste sind ab dann praktisch ausgeschlossen.',
  },
  theme_factor: {
    short: 'Theme-Faktor',
    label: 'Konzentrations-Abschlag',
    explanation:
      'Score-Abschlag wenn zu viele Symbole im gleichen Theme/Sektor sind — verhindert Klumpenrisiko.',
  },
  confidence: {
    short: 'Confidence',
    label: 'Score-Konfidenz',
    explanation:
      'Wie verlässlich ist die Bewertung? Sinkt automatisch wenn Quellen-Daten alt werden (Welle M13 Decay).',
  },
  phase_fit: {
    short: 'Phasen-Fit',
    label: 'Markt-Passung',
    explanation:
      'Passt die Strategie zur aktuellen Marktphase? Pullback-Setup im Bärenmarkt = niedriger Phasen-Fit.',
  },
  asset_fit: {
    short: 'Asset-Fit',
    label: 'Asset-Passung',
    explanation:
      'Passt die Strategie zum Asset-Typ? ETF-Rotation für eine Einzelaktie = niedriger Asset-Fit.',
  },
  quintile: {
    short: 'Quintile',
    label: 'Rang-Klasse',
    explanation:
      'Q1 = Top 20 % deiner Watchlist, Q5 = Bottom 20 %. Zeigt wie das Symbol gegen Deine eigenen Vergleichswerte abschneidet.',
  },

  // ===== Fundamental-Kennzahlen (Klartext-Erklärungen) =====
  roic: {
    short: 'ROIC',
    label: 'Kapital-Rendite',
    explanation:
      'Return on Invested Capital — wie viel Gewinn pro € eingesetztes Kapital? >15 % über mehrere Jahre = solider Burggraben.',
  },
  fcf: {
    short: 'FCF',
    label: 'Freier Cashflow',
    explanation:
      'Echter Geldfluss nach allen Investitionen — schlägt Buchgewinn. Negativ über Jahre = Warnsignal.',
  },
  fcf_margin: {
    short: 'FCF-Marge',
    label: 'Cash-Marge',
    explanation:
      'Wie viel vom Umsatz bleibt am Ende als Cash übrig? >15 % = Cash-Maschine.',
  },
  roe: {
    short: 'ROE',
    label: 'Eigenkapital-Rendite',
    explanation:
      'Gewinn pro € Eigenkapital. >15 % = profitabel, >25 % = sehr stark.',
  },
  pe_ttm: {
    short: 'KGV (TTM)',
    label: 'Kurs-Gewinn-Verhältnis (rollierend)',
    explanation:
      'Aktienkurs geteilt durch Gewinn pro Aktie der letzten 12 Monate. <15 = günstig, >30 = teuer (kontext-abhängig).',
  },
  kbv: {
    short: 'KBV',
    label: 'Kurs-Buchwert',
    explanation:
      'Wie teuer ist die Aktie relativ zum bilanziellen Eigenkapital? <1.5 = klassisches Value-Signal.',
  },
  debt_to_equity: {
    short: 'D/E',
    label: 'Verschuldungsgrad',
    explanation:
      'Schulden geteilt durch Eigenkapital. <0.5 = solide, >1.5 = riskant bei Zinswende.',
  },
  payout_ratio: {
    short: 'Payout-Ratio',
    label: 'Ausschüttungsquote',
    explanation:
      'Anteil des Gewinns der als Dividende ausgezahlt wird. >80 % = Dividende kaum nachhaltig, Kürzungsrisiko.',
  },
};

/**
 * Hilfsfunktion: gibt für einen Begriff den Plain-Label und die Erklärung zurück.
 * Fallback: short = label = key.
 */
export function term(key: keyof typeof GLOSSARY): GlossaryEntry {
  const entry = GLOSSARY[key];
  if (!entry) {
    return { short: String(key), label: String(key), explanation: '' };
  }
  return entry;
}

/**
 * Score-Einordnung im Spezialist-Ton (Verständlichkeits-Welle Teil 4).
 * Direkt, ehrlich, handlungsorientiert — wie ein Berater mit 20 Jahren Erfahrung.
 */
export function scoreMeaning(score: number): { tier: string; meaning: string } {
  if (score < 40) {
    return {
      tier: 'Derzeit uninteressant',
      meaning: 'Zu viele Faktoren sprechen dagegen.',
    };
  }
  if (score < 55) {
    return {
      tier: 'Schwaches Signal',
      meaning: 'Einzelne positive Anzeichen, aber kein klares Bild.',
    };
  }
  if (score < 65) {
    return {
      tier: 'Beobachten',
      meaning: 'Grundlage stimmt, aber noch kein konkretes Einstiegssignal.',
    };
  }
  if (score < 75) {
    return {
      tier: 'Solide',
      meaning: 'Mehrere Faktoren stimmen — Timing und Chart prüfen.',
    };
  }
  if (score < 85) {
    return {
      tier: 'Stark',
      meaning: 'Seltenes Signal — Bedingungen bestätigen sich gegenseitig.',
    };
  }
  return {
    tier: 'Ausnahmesituation',
    meaning: 'Alle Faktoren ausgerichtet, selten und aussagekräftig.',
  };
}

/**
 * V5.1-Strategie-Beschreibungen (Verständlichkeits-Welle Teil 1).
 * 1-Satz-Erklärung in Alltagssprache pro Strategie.
 */
export interface StrategyExplanation {
  id: string;
  label: string;
  shortLabel: string;
  oneLineExplanation: string;
  /**
   * Audit 14.06.: true = diese Strategie kauft IN die Stärke (über einem Hoch /
   * direkt nach Earnings-Sprung) — das Gegenteil des antizyklischen Stils.
   * Die UI zeigt dann einen Hinweis (User-Entscheidung: kennzeichnen, nicht ausblenden).
   */
  procyclical?: boolean;
}

/**
 * Hinweis-Text für prozyklische (Trendfolge-)Strategien — kaufen IN die Stärke.
 * CHANCE-formuliert (User 16.06.): Ronnie ist im Kern antizyklisch, nimmt aber
 * bei gutem Markt bewusst auch starke Trendfolge-Signale mit und will sie NICHT
 * übersehen. Darum ein Chancen-Tag (sky/blau, KEIN Warn-Amber) mit positivem
 * Wortlaut statt „nicht dein Einstieg"-Vorbehalt. Siehe [[user-profile]].
 */
export const PROCYCLICAL_HINT =
  'Trendfolge: kauft in Stärke. Kein antizyklischer Boden-Kauf — aber bei gutem Markt oft ein starkes Signal zum Mitnehmen.';

export const STRATEGY_EXPLANATIONS: Record<string, StrategyExplanation> = {
  breakout: {
    id: 'breakout',
    label: 'S1 Breakout / Trendfolge',
    shortLabel: 'Breakout',
    oneLineExplanation:
      'Diese Aktie bricht aus einer Seitwärtsphase nach oben aus — der Kurs gewinnt Fahrt und wir steigen mit dem Trend ein.',
    procyclical: true,
  },
  pullback: {
    id: 'pullback',
    label: 'S2 Pullback',
    shortLabel: 'Pullback',
    oneLineExplanation:
      'Die Aktie ist im Aufwärtstrend, hat aber kurz nachgegeben — ein günstiger Einstieg bevor der Trend weitergeht.',
  },
  relative_strength: {
    id: 'relative_strength',
    label: 'S3 Relative Stärke',
    shortLabel: 'Relative Stärke',
    oneLineExplanation:
      'Diese Aktie steigt stärker als der Gesamtmarkt — ein Zeichen dass Großanleger gezielt hier kaufen.',
    procyclical: true,
  },
  event: {
    id: 'event',
    label: 'S4 Event / Earnings',
    shortLabel: 'Event',
    oneLineExplanation:
      'Reaktion auf ein Quartals-Ergebnis: stark überraschende Zahlen mit positiver Kursreaktion — wir steigen kurz nach dem Bericht ein.',
    procyclical: true,
  },
  longterm: {
    id: 'longterm',
    label: 'S5 Langfrist-Aufbau',
    shortLabel: 'Langfrist',
    oneLineExplanation:
      'Qualitätsunternehmen mit stabilem Geschäftsmodell — zum schrittweisen Aufbauen über Monate bis Jahre, nicht für schnelle Trades.',
  },
  etf_rotation: {
    id: 'etf_rotation',
    label: 'S6 ETF-Rotation',
    shortLabel: 'ETF-Rotation',
    oneLineExplanation:
      'Wir investieren in die Branchen und Märkte die gerade am stärksten laufen — und wechseln wenn sich die Führung ändert.',
    // S2 (Score-Audit 06.08.2026): fehlte hier, obwohl phantom-core.ts die
    // Strategie als prozyklisch führt und den Phantom-Plan im Bull ablehnt.
    // Folge: In Watchlist und Multi-Score-Panel erschien bei QQQ KEIN
    // Trendfolge-Hinweis — genau dort, wo der User scannt.
    procyclical: true,
  },
  defensive: {
    id: 'defensive',
    label: 'S7 Defensive',
    shortLabel: 'Defensive',
    oneLineExplanation:
      'Werte die in fallenden Märkten typischerweise weniger verlieren — Gold, Versorger, Basiskonsumgüter — als Schutz oder Beimischung.',
  },
  crypto: {
    id: 'crypto',
    label: 'S8 Krypto',
    shortLabel: 'Krypto',
    oneLineExplanation:
      'Donchian-Ausbruch bei Krypto-Top-Coins — wir folgen großen Bewegungen, nicht jeder kleinen Schwankung.',
  },
  // W91 — antizyklischer Tranchen-Plan (Stage-4-Boden, kein eigener S-Slot)
  antizyklisch: {
    id: 'antizyklisch',
    label: 'Antizyklischer Tranchen-Plan',
    shortLabel: 'Antizyklisch',
    oneLineExplanation:
      'Qualität tief kaufen: Tranche 1 mit kleinem Risiko in der Boden-Zone, Tranchen 2/3 erst bei tieferen Levels oder Reversal-Bestätigung.',
  },
  // W93 (#146) — Krypto-Zyklus-Akkumulation (Boden-Score t1_ready)
  krypto_boden: {
    id: 'krypto_boden',
    label: 'Krypto-Akkumulation (Zyklus-Boden)',
    shortLabel: 'Krypto-Boden',
    oneLineExplanation:
      'Bärenmarkt-Akkumulation an der 200W-MA: Tranche 1 mit ⅓-Risiko, Tranchen 2/3 an den tieferen Zyklus-Fib-Zonen — kein Timing-Signal, sondern Zonen-Kauf.',
  },
};

/**
 * Marktphasen-Handlungsempfehlung (Verständlichkeits-Welle Teil 5).
 * Ergänzt die kurze Phasen-Beschreibung um eine direkte Handlung.
 */
export const PHASE_ADVICE: Record<string, { headline: string; advice: string }> = {
  bull: {
    headline: 'Aufwärtstrend',
    advice:
      'Der Markt läuft gut. Trend-Strategien funktionieren. Normale Positionsgrößen sind angemessen — kein Grund zur Zurückhaltung.',
  },
  korrektur: {
    headline: 'Korrektur',
    advice:
      'Der Markt schwächelt, ist aber grundsätzlich noch gesund. Ich würde neue Käufe reduzieren und lieber warten bis sich der Staub legt — oder nur in die stärksten Aktien einsteigen.',
  },
  neutral: {
    headline: 'Unentschlossen',
    advice:
      'Kein klarer Trend. Hier keine großen Wetten eingehen. Nur Aktien kaufen die deutlich stärker als der Gesamtmarkt sind — alle anderen: abwarten.',
  },
  bear: {
    headline: 'Abwärtstrend',
    advice:
      'Der Markt fällt. Neue Käufe sind jetzt gefährlich. Bestehende Positionen absichern. Cash halten ist eine legitime Strategie.',
  },
  accumulation: {
    headline: 'Bodenbildung',
    advice:
      'Möglicher Wendepunkt. Erste vorsichtige Käufe in Qualitätsunternehmen sind vertretbar — aber nur kleine Positionen, die Richtung ist noch nicht bestätigt.',
  },
};
